// Overview — landing page after login. Shows which presets start
// after a restart, a snapshot of active runs, and quick-link cards.
// Operators glance here to confirm the fleet is running as expected
// before drilling into specific runs.
//
// The "default preset" card became an auto-start card (SM-538): a
// deployment can arm several presets, so a single-name card could only
// ever tell part of the truth.
import { createFileRoute, Link } from "@tanstack/react-router";
import { ROUTE_CRITICAL_KEYS, refreshOnNavigate } from "@/lib/routeRefresh";
import { DataFreshness } from "@/components/ui/DataFreshness";
import { useQuery } from "@tanstack/react-query";
import { keys, fetchCapabilities, fetchPresets, fetchRuns } from "@/lib/queries";
import { Card } from "@/components/ui/Card";
import { Badge, StatusBadge } from "@/components/ui/Badge";
import { CurrencyAmount } from "@/components/ui/CurrencyAmount";
import { ModeBadge } from "@/components/runs/RunStateTabs";
import { Skeleton } from "@/components/ui/Skeleton";
import { useI18n } from "@/i18n/I18nProvider";
import { effectiveTradingMode } from "@/lib/tradingMode";
import { gameStrategiesEnabled } from "@/lib/featureFlags";
import { strategyNameLabel } from "@/i18n/strategyCopy";

export const Route = createFileRoute("/_app/")({
  // SM-563 — re-read this page's data on arrival.
  loader: refreshOnNavigate(ROUTE_CRITICAL_KEYS.overview),
  component: OverviewPage,
});

function OverviewPage() {
  const { t, formatDateTime } = useI18n();
  // Every armed preset, not "the default": the list read carries the
  // flag, so no separate endpoint is needed and the card can show all
  // of them.
  const presetsQ = useQuery({
    queryKey: keys.presets(200, 0),
    queryFn: () => fetchPresets(200, 0),
  });
  const autoStartPresets = (presetsQ.data?.presets ?? []).filter((p) => p.auto_start_enabled);
  const runsQ = useQuery({
    queryKey: keys.runs(undefined, 10, 0),
    queryFn: () => fetchRuns(undefined, 10, 0),
    refetchInterval: 5_000,
  });

  // The runtime's EFFECTIVE switches (SM-531 / SM-506), shown beside this
  // console's own build flag. The two halves of "can a GAME preset
  // start" live in different deploys; putting both on one card is what
  // makes a drift between them a thing an operator can see.
  const capsQ = useQuery({ queryKey: keys.capabilities(), queryFn: fetchCapabilities, staleTime: 60_000 });

  const activeRuns = runsQ.data?.runs.filter((r) => r.is_active) ?? [];
  // Per MODE, never summed into one "active" figure (SM-473/G8): a
  // CASH run and a GAME run trade different currencies on different
  // books, and "3 running" hides which three.
  const activeCash = activeRuns.filter((r) => effectiveTradingMode(r.config) === "CASH");
  const activeGame = activeRuns.filter((r) => effectiveTradingMode(r.config) === "GAME");

  return (
    <div className="space-y-6">
      <header>
        <h1 className="page-title">{t("app.name")}</h1>
        <p className="page-subtitle">
          {t("overview.subtitle")}
        </p>
      </header>
      <DataFreshness query={runsQ} relatedQueries={[presetsQ, capsQ]} />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Card>
          <div className="text-2xs uppercase tracking-widest text-t-3">
            {t("overview.autoStartPresets")}
          </div>
          {presetsQ.isLoading && <Skeleton lines={2} />}
          {!presetsQ.isLoading && autoStartPresets.length === 0 && (
            <>
              <div className="mt-2 text-lg font-semibold text-t-1">—</div>
              <p className="mt-1 text-xs text-t-3">{t("overview.noAutoStart")}</p>
            </>
          )}
          {autoStartPresets.length > 0 && (
            <ul className="mt-2 space-y-1.5">
              {autoStartPresets.map((p) => (
                <li key={p.preset_id} className="flex flex-wrap items-center gap-1.5">
                  <Link
                    to="/presets/$id"
                    params={{ id: p.preset_id }}
                    className="max-w-56 truncate text-sm font-semibold text-t-1 hover:t-emph"
                  >
                    {p.name}
                  </Link>
                  <ModeBadge mode={effectiveTradingMode(p.config)} />
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <div className="text-2xs uppercase tracking-widest text-t-3">{t("overview.activeRuns")}</div>
          <div className="mt-2 font-mono text-2xl font-bold text-t-1">
            {runsQ.isLoading ? "—" : activeRuns.length}
          </div>
          <p className="mt-1 text-xs text-t-3">
            {activeRuns.length === 0
              ? t("overview.fleetIdle")
              : t("overview.runsDriving", { count: activeRuns.length })}
          </p>
          {/* The split, when there is one to show. A GAME line on a
              fleet with no GAME runs would be noise; its absence is
              information. */}
          {!runsQ.isLoading && activeRuns.length > 0 && (
            <dl className="mt-2 space-y-0.5 text-2xs">
              <div className="flex justify-between gap-2">
                <dt className="text-t-3">{t("overview.activeCash")}</dt>
                <dd className="mono text-t-1">{activeCash.length}</dd>
              </div>
              {(activeGame.length > 0 || gameStrategiesEnabled()) && (
                <div className="flex justify-between gap-2">
                  <dt className="text-t-3">{t("overview.activeGame")}</dt>
                  <dd className="mono text-t-1">{activeGame.length}</dd>
                </div>
              )}
            </dl>
          )}
        </Card>

        <Card>
          <div className="text-2xs uppercase tracking-widest text-t-3">{t("capabilities.title")}</div>
          <p className="mt-1 text-xs text-t-3">{t("capabilities.subtitle")}</p>
          {capsQ.isLoading && <Skeleton lines={3} />}
          {!capsQ.isLoading && !capsQ.data && (
            <p className="mt-2 text-xs t-mute">{t("capabilities.unavailable")}</p>
          )}
          {capsQ.data && (
            <dl className="mt-2 space-y-1 text-2xs">
              <CapabilityRow label={t("capabilities.gameRuntime")} on={capsQ.data.game_trading && capsQ.data.game_visibility} />
              <CapabilityRow label={t("capabilities.gameConsole")} on={gameStrategiesEnabled()} />
              <CapabilityRow label={t("capabilities.autoTopUp")} on={capsQ.data.funding_auto_top_up_from_parent} />
              {capsQ.data.funding_auto_top_up_from_parent && (
                <div className="flex justify-between gap-2">
                  <dt className="text-t-3">{t("capabilities.autoTopUpCeiling")}</dt>
                  <dd className="mono text-t-1">
                    {capsQ.data.funding_auto_top_up_max_minor > 0 ? (
                      <CurrencyAmount currency="GOLD" value={capsQ.data.funding_auto_top_up_max_minor} hideIcon />
                    ) : (
                      t("capabilities.noCeiling")
                    )}
                  </dd>
                </div>
              )}
              <div className="flex justify-between gap-2">
                <dt className="text-t-3">{t("capabilities.fundingMode")}</dt>
                <dd className="mono text-t-1">
                  {capsQ.data.funding_mode}
                  {capsQ.data.funding_mode === "system_transfer" && ` · ${capsQ.data.funding_account_code}`}
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-t-3">{t("capabilities.maxRuns")}</dt>
                <dd className="mono text-t-1">{capsQ.data.max_concurrent_runs}</dd>
              </div>
              {(capsQ.data.game_trading && capsQ.data.game_visibility) !== gameStrategiesEnabled() && (
                <p className="mt-1 text-xs" style={{ color: "var(--warn)" }}>
                  {t("capabilities.drift")}
                </p>
              )}
            </dl>
          )}
        </Card>

        <Card>
          <div className="text-2xs uppercase tracking-widest text-t-3">{t("overview.quickActions")}</div>
          <ul className="mt-2 space-y-1.5 text-sm">
            <li><Link to="/runs" className="text-t-1 hover:t-emph">{t("overview.viewRuns")}</Link></li>
            <li><Link to="/presets" className="text-t-1 hover:t-emph">{t("overview.editPresets")}</Link></li>
            <li><Link to="/strategies" className="text-t-1 hover:t-emph">{t("overview.browseStrategies")}</Link></li>
          </ul>
        </Card>
      </div>

      <Card>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="section-title">{t("overview.recentRuns")}</h3>
          <Link to="/runs" className="text-2xs t-emph hover:underline">{t("overview.viewAll")}</Link>
        </div>
        {runsQ.isLoading ? (
          <Skeleton lines={4} />
        ) : runsQ.data?.runs.length === 0 ? (
          <div className="empty">{t("overview.noRuns")}</div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>{t("runs.run")}</th>
                <th>{t("common.status")}</th>
                <th>{t("overview.strategy")}</th>
                {/* Which book a run traded is not derivable from its
                    strategy name, and a GAME run's numbers are RED and
                    GREEN — so the column is not optional. */}
                <th>{t("presets.tradingMode")}</th>
                <th>{t("overview.started")}</th>
                <th>{t("overview.stopped")}</th>
              </tr>
            </thead>
            <tbody>
              {runsQ.data?.runs.slice(0, 8).map((r) => (
                <tr key={r.run_id}>
                  <td>
                    {/* Name first. The id stays on the row as secondary
                        context, so an operator can still copy it. */}
                    <Link
                      to="/runs/$id"
                      params={{ id: r.run_id }}
                      className="block max-w-64 truncate hover:t-emph"
                    >
                      {r.preset_name || r.run_id}
                    </Link>
                    <span className="mono block text-[11px] text-t-4" title={r.run_id}>
                      {r.run_id.slice(0, 12)}
                    </span>
                  </td>
                  <td>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <StatusBadge status={r.status} />
                      {(r.recovery?.attempt ?? 1) > 1 && (
                        <Badge tone="warn">
                          {t("history.attemptOf", {
                            attempt: r.recovery.attempt,
                            source: r.recovery.resumed_from_run_id?.slice(0, 8) ?? "—",
                          })}
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td>{strategyNameLabel(r.config.strategy.name, t)}</td>
                  <td className="text-xs">
                    <ModeBadge mode={r.trading_mode ?? effectiveTradingMode(r.config)} />
                  </td>
                  <td className="mono text-xs">{r.start_at ? formatDateTime(r.start_at) : "—"}</td>
                  <td className="mono text-xs">{r.stop_at ? formatDateTime(r.stop_at) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

// One on/off line of the capabilities card. The word carries the
// meaning; the tone only reinforces it.
function CapabilityRow({ label, on }: { label: string; on: boolean }) {
  const { t } = useI18n();
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-t-3">{label}</dt>
      <dd>
        <Badge tone={on ? "yes" : "neutral"}>{on ? t("capabilities.on") : t("capabilities.off")}</Badge>
      </dd>
    </div>
  );
}
